import React, { useMemo, useState } from 'react';
import { Search, Download, FileText, ShieldCheck, Ban, Trash2, Archive, ArchiveRestore, FilePlus2, Pencil, Printer, CheckCircle2, Activity, LogIn, LogOut, FileDown, User, ChevronRight, X, ArrowRight, AlertTriangle, Mail } from 'lucide-react';
import type { AppState, AccionAuditoria, AuditEvent } from '../types';
import { PageShell, glassLight, inputStyle, ghostButton, EmptyState, StatCard, usePagination, Pagination, useIsMobile, Modal, T } from '../ui';
import { csvCell, downloadText } from '../storage';
import { ETIQUETA_ACCION, diaDe, horaDe } from '../auditoria';

// ── CÓMO SE VE CADA ACCIÓN ──────────────────────────────────────────────────
// El color y el ícono los pone la acción, que ahora viene guardada en el
// evento. Los eventos viejos no la traen: para esos se deduce de la frase al
// leer la fila (ver `normalizarEvento` en auditoria.ts), así que aquí ya llega
// resuelta en todos los casos.
const ASPECTO: Record<AccionAuditoria, {
  color: string;
  soft: string;
  icono: React.ElementType;
}> = {
  crear: {
    color: T.ok,
    soft: T.okSoft,
    icono: FilePlus2
  },
  editar: {
    color: T.info,
    soft: T.infoSoft,
    icono: Pencil
  },
  eliminar: {
    color: T.danger,
    soft: T.dangerSoft,
    icono: Trash2
  },
  archivar: {
    color: T.warn,
    soft: T.warnSoft,
    icono: Archive
  },
  desarchivar: {
    color: T.info,
    soft: T.infoSoft,
    icono: ArchiveRestore
  },
  autorizar: {
    color: T.ok,
    soft: T.okSoft,
    icono: ShieldCheck
  },
  anular: {
    color: T.danger,
    soft: T.dangerSoft,
    icono: Ban
  },
  imprimir: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: Printer
  },
  exportar: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: FileDown
  },
  correo: {
    color: T.info,
    soft: T.infoSoft,
    icono: Mail
  },
  resolver: {
    color: T.info,
    soft: T.infoSoft,
    icono: CheckCircle2
  },
  entrar: {
    color: T.brand,
    soft: T.accentSoft,
    icono: LogIn
  },
  salir: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: LogOut
  },
  otro: {
    color: T.textSecondary,
    soft: T.surfaceSubtle,
    icono: Activity
  }
};

// Orden en que se ofrecen los filtros: primero lo que cambia datos, después lo
// que solo los consulta o mueve.
const ORDEN_ACCIONES: AccionAuditoria[] = ['crear', 'editar', 'archivar', 'desarchivar', 'autorizar', 'anular', 'eliminar', 'resolver', 'imprimir', 'exportar', 'correo', 'entrar', 'salir', 'otro'];

// Iniciales para el redondelito del autor. Dos palabras como mucho: "José Raúl
// Peralta" → "JR".
function iniciales(nombre: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  return (partes[0][0] + (partes[1]?.[0] ?? '')).toUpperCase();
}

// ── DETALLE DE UN EVENTO ────────────────────────────────────────────────────
// Se abre al pulsar la fila. Responde a las cuatro preguntas de una auditoría:
// quién, qué hizo, sobre qué, y qué cambió exactamente.
function DetalleEvento({
  evento,
  onClose
}: {
  evento: AuditEvent;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const aspecto = ASPECTO[evento.accion];
  const Icono = aspecto.icono;
  const dato = (etiqueta: string, valor: React.ReactNode) => <div data-id="element-151" data-mp="3ecr89kutqdwg">
      <div style={{
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 3
    }} data-id="element-152" data-mp="3ed0z1anp0p37">

        {etiqueta}
      </div>
      <div style={{
      fontSize: 13.5,
      fontWeight: 600,
      color: T.text,
      wordBreak: 'break-word'
    }} data-id="element-153" data-mp="avr6t2vfh1ho">

        {valor}
      </div>
    </div>;
  return <Modal onClose={onClose} title="Detalle del evento" wide data-id="element-154" data-mp="vrzf9aqvh6e">
      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 18
    }} data-id="element-155" data-mp="2qm40r0otlqaf">

        <span style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 38,
        height: 38,
        borderRadius: 12,
        flexShrink: 0,
        background: aspecto.soft,
        border: `1px solid ${aspecto.color}33`,
        color: aspecto.color
      }} data-id="element-156" data-mp="2lmizukq3b7v2">

          <Icono size={18} data-id="element-157" data-mp="4ez7lmkpr8ad" />
        </span>
        <div style={{
        minWidth: 0
      }} data-id="element-158" data-mp="4btja9jwsafp">
          <div style={{
          fontSize: 15,
          fontWeight: 700,
          color: T.text,
          lineHeight: 1.35,
          wordBreak: 'break-word'
        }} data-id="element-159" data-mp="9dsjjakcqr8f">

            {evento.mensaje}
          </div>
          <div style={{
          fontSize: 11.5,
          fontWeight: 700,
          color: aspecto.color,
          marginTop: 2
        }} data-id="element-160" data-mp="120wml9zvwvhk">

            {ETIQUETA_ACCION[evento.accion]}
          </div>
        </div>
      </div>

      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
      gap: 14,
      padding: '14px 16px',
      borderRadius: 12,
      background: T.surfaceSubtle,
      marginBottom: 18
    }} data-id="element-161" data-mp="37ud10i4ynxw9">

        {dato('Quién', evento.usuario || 'Sin registrar')}
        {dato('Qué', ETIQUETA_ACCION[evento.accion])}
        {dato('Entidad', evento.entidad || '—')}
        {dato('Registro', evento.registro || '—')}
      </div>

      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: 14,
      marginBottom: 18
    }} data-id="element-162" data-mp="23uf0m7eqxld0">

        {dato('Cuándo', evento.sello || '—')}
        {dato('Fecha exacta', evento.fechaISO ? new Date(evento.fechaISO).toLocaleString('es-DO') : 'No registrada')}
      </div>

      {/* ── QUÉ CAMBIÓ ── */}
      <div style={{
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: '.1em',
      textTransform: 'uppercase',
      color: T.textMuted,
      marginBottom: 8
    }} data-id="element-163" data-mp="1kxx4zews11bh">

        {evento.accion === 'crear' ? 'Con qué se creó' : 'Qué cambió'}
      </div>

      {evento.cambios.length === 0 ? <div style={{
      fontSize: 12.5,
      color: T.textMuted,
      padding: '14px 16px',
      borderRadius: 12,
      border: `1px dashed ${T.border}`,
      lineHeight: 1.5
    }} data-id="element-164" data-mp="oepop1grosqw">

          Este evento no guardó el detalle de los campos. Los eventos anteriores
          a esta versión del sistema solo tienen la frase; los nuevos sí lo
          traen.
        </div> : <div style={{
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      overflow: 'hidden'
    }} data-id="element-165" data-mp="3295eub6uv29a">

          {evento.cambios.map((c, i) => <div key={i} style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,150px) minmax(0,1fr) 16px minmax(0,1fr)',
        gap: isMobile ? 4 : 12,
        alignItems: 'center',
        padding: '11px 14px',
        borderTop: i === 0 ? 'none' : `1px solid ${T.border}`,
        background: i % 2 ? T.surfaceSubtle : 'transparent'
      }} data-id="element-166" data-mp="24n7s21lg0arj" data-mpi={i}>

              <span style={{
          fontSize: 12,
          fontWeight: 700,
          color: T.textSecondary
        }} data-id="element-167" data-mp="3py7w3hxkut39">
                {c.campo}
              </span>

              {/* En un alta no hay valor anterior: se muestra solo con qué
               nació, sin la flecha ni el hueco de "antes". */}
              {c.antes === '' ? <span style={{
          fontSize: 13,
          color: T.text,
          fontWeight: 600,
          gridColumn: isMobile ? 'auto' : '2 / -1',
          wordBreak: 'break-word'
        }} data-id="element-168" data-mp="1edqssry8zi48">
                  {c.despues}
                </span> : <>
                  <span style={{
            fontSize: 13,
            color: T.textMuted,
            textDecoration: 'line-through',
            wordBreak: 'break-word'
          }} data-id="element-169" data-mp="znjaqdc5k142">
                    {c.antes}
                  </span>
                  <ArrowRight size={14} style={{
            color: T.textMuted,
            flexShrink: 0,
            display: isMobile ? 'none' : 'block'
          }} data-id="element-170" data-mp="qnwj2j9869o7" />

                  <span style={{
            fontSize: 13,
            color: T.text,
            fontWeight: 700,
            wordBreak: 'break-word'
          }} data-id="element-171" data-mp="2kzkre2kybdzm">
                    {c.despues}
                  </span>
                </>}
            </div>)}
        </div>}

      <div style={{
      marginTop: 18,
      display: 'flex',
      justifyContent: 'flex-end'
    }} data-id="element-172" data-mp="1tv08wmf972hr">
        <button type="button" onClick={onClose} style={ghostButton} data-id="element-173" data-mp="1f4t0kziymzus">
          <X size={14} data-id="element-174" data-mp="1k4nrv46ndrgd" /> Cerrar
        </button>
      </div>
    </Modal>;
}
export function Auditoria({
  state
}: {
  state: AppState;
}) {
  const isMobile = useIsMobile();
  const [busqueda, setBusqueda] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('todos');
  const [filtroUsuario, setFiltroUsuario] = useState('todos');
  const [abierto, setAbierto] = useState<AuditEvent | null>(null);

  // `state.audit` ya viene con el más reciente primero y normalizado: la
  // pantalla no tiene que saber de qué época es cada fila.
  const eventos: AuditEvent[] = useMemo(() => Array.isArray(state.audit) ? state.audit : [], [state.audit]);

  // Solo se ofrecen como filtro las acciones y los autores que aparecen de
  // verdad en el registro.
  const accionesPresentes = useMemo(() => {
    const vistas = new Set(eventos.map(e => e.accion));
    return ORDEN_ACCIONES.filter(a => vistas.has(a));
  }, [eventos]);
  const usuariosPresentes = useMemo(() => [...new Set(eventos.map(e => e.usuario).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es')), [eventos]);
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return eventos.filter(e => filtroAccion === 'todos' || e.accion === filtroAccion).filter(e => filtroUsuario === 'todos' || e.usuario === filtroUsuario).filter(e => {
      if (!q) return true;
      // La búsqueda también entra en el detalle: así se puede buscar un valor
      // concreto ("L-317355") y encontrar el evento que lo cambió.
      const enCambios = e.cambios.some(c => `${c.campo} ${c.antes} ${c.despues}`.toLowerCase().includes(q));
      return `${e.completo} ${e.usuario} ${e.entidad} ${e.registro}`.toLowerCase().includes(q) || enCambios;
    });
  }, [eventos, busqueda, filtroAccion, filtroUsuario]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filtrados);

  // Día más reciente del registro: sirve para destacar "lo de hoy" sin
  // depender de la fecha real del navegador, porque el sello no trae año.
  const diaReciente = eventos.length ? diaDe(eventos[0].sello) : '';
  const stats = useMemo(() => ({
    total: eventos.length,
    recientes: eventos.filter(e => diaDe(e.sello) === diaReciente).length,
    cambios: eventos.filter(e => ['crear', 'editar', 'eliminar', 'archivar', 'desarchivar'].includes(e.accion)).length
  }), [eventos, diaReciente]);
  const exportar = () => {
    // El detalle también se exporta: una línea por campo cambiado, repitiendo
    // los datos del evento. Un CSV con el detalle metido en una sola celda no
    // se puede filtrar ni sumar, que es para lo que se exporta.
    const filas: string[][] = [['Fecha', 'Usuario', 'Acción', 'Entidad', 'Registro', 'Detalle', 'Campo', 'Antes', 'Después']];
    for (const e of filtrados) {
      const base = [e.sello, e.usuario, ETIQUETA_ACCION[e.accion], e.entidad, e.registro, e.mensaje];
      if (!e.cambios.length) filas.push([...base, '', '', '']);else for (const c of e.cambios) filas.push([...base, c.campo, c.antes, c.despues]);
    }
    const csv = filas.map(f => f.map(csvCell).join(',')).join('\n');
    // BOM para que Excel abra bien los acentos.
    downloadText('auditoria.csv', '﻿' + csv, 'text/csv;charset=utf-8');
  };

  // Los eventos de la página se agrupan por día, conservando el orden.
  const grupos = useMemo(() => {
    const salida: {
      dia: string;
      items: AuditEvent[];
    }[] = [];
    for (const e of pageItems) {
      const dia = diaDe(e.sello);
      const ultimo = salida[salida.length - 1];
      if (ultimo && ultimo.dia === dia) ultimo.items.push(e);else salida.push({
        dia,
        items: [e]
      });
    }
    return salida;
  }, [pageItems]);
  return <PageShell title="Auditoría" sub="Quién hizo qué, cuándo y qué cambió exactamente" action={eventos.length > 0 ? <button onClick={exportar} style={ghostButton} data-id="element-176" data-mp="37lp7dhr4og49">
            <Download size={14} data-id="element-177" data-mp="3md7vy3y1ziz8" /> Exportar CSV
          </button> : undefined} data-id="element-175" data-mp="3cmwinqshbxfo">

      <style data-id="element-178" data-mp="3hl7ozrjkfpt5">{`
        .aud-dia { position: sticky; top: 0; z-index: 2; backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
        .aud-row { position: relative; display: grid; align-items: start; transition: background .15s; width: 100%; text-align: left; border: 0; background: transparent; font-family: inherit; cursor: pointer; }
        .aud-row:hover { background: ${T.surfaceHover}; }
        .aud-row:focus-visible { outline: 2px solid ${T.accent}; outline-offset: -2px; }
        /* La flecha solo se destaca en la fila donde esta el puntero: si se
           viera siempre, catorce flechas competirian con el texto. */
        .aud-row .aud-ir { opacity: .35; transition: opacity .15s, transform .15s; }
        .aud-row:hover .aud-ir { opacity: 1; transform: translateX(2px); }
        /* Riel vertical que une los marcadores. Se recorta en el primero y
           el ultimo de cada dia para que no sobresalga del grupo. */
        .aud-row::before { content: ''; position: absolute; left: var(--riel); top: 0; bottom: 0; width: 1px; background: ${T.border}; }
        .aud-row:first-of-type::before { top: 26px; }
        .aud-row:last-of-type::before { bottom: calc(100% - 26px); }
        .aud-mark { position: relative; z-index: 1; display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0; }
      `}</style>

      {/* Sin las columnas nuevas, cada evento se guarda con la frase y nada
       más: el autor y el detalle se pierden en el momento de guardar, no al
       mostrarlos. Antes eso pasaba en silencio y lo único que se veía era
       "Sin registrar" en todo, sin ninguna pista de por qué. */}
      {!state.auditDetalleActivo && <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 11,
      padding: '13px 16px',
      borderRadius: 14,
      marginBottom: 18,
      background: T.warnSoft,
      border: `1px solid ${T.warn}44`
    }} data-id="element-179" data-mp="3o6fhaxdywloz">

          <AlertTriangle size={17} style={{
        color: T.warn,
        flexShrink: 0,
        marginTop: 1
      }} data-id="element-180" data-mp="3a7xztheno5fi" />

          <div style={{
        fontSize: 12.5,
        lineHeight: 1.55,
        color: T.textSecondary
      }} data-id="element-181" data-mp="3594ljmrjoaea">
            <strong style={{
          color: T.text
        }} data-id="element-182" data-mp="30h3bm7humvrf">
              Los eventos se están guardando sin autor ni detalle.
            </strong>{' '}
            A la tabla <code data-id="element-183" data-mp="2rhq2jfxn0978">auditoria</code> de Supabase le faltan las columnas
            que guardan quién hizo cada cosa y qué campos cambió. Corre{' '}
            <code data-id="element-184" data-mp="2mhvctfpllpnr">sql/auditoria_detallada.sql</code> en el SQL Editor y recarga
            la página. Lo que se registre mientras tanto quedará como
            &ldquo;Sin registrar&rdquo; para siempre.
          </div>
        </div>}

      {eventos.length === 0 ? <EmptyState title="Sin eventos registrados" hint="Las acciones y cambios del sistema aparecerán aquí en cuanto ocurran." icon={<FileText size={22} data-id="element-186" data-mp="2heks0qt947wn" />} data-id="element-185" data-mp="2cdx1s7cujv3h" /> : <>
          <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0,1fr))',
        gap: 14,
        marginBottom: 22
      }} data-id="element-187" data-mp="2hbgiddx0ojyl">

            <StatCard label="Eventos" value={String(stats.total)} note="en el registro" data-id="element-188" data-mp="1oqf2lqr95uqh" />

            <StatCard label={diaReciente ? `Actividad del ${diaReciente}` : 'Último día'} value={String(stats.recientes)} note="eventos" accent={T.info} data-id="element-189" data-mp="1togn8g4bu242" />

            <StatCard label="Cambios de datos" value={String(stats.cambios)} note="altas, ediciones y archivos" accent={T.warn} data-id="element-190" data-mp="23di56bufmtko" />

          </div>

          {/* ── FILTROS ── */}
          <div style={{
        display: 'flex',
        gap: 10,
        flexWrap: 'wrap',
        marginBottom: 14
      }} data-id="element-191" data-mp="15wjl5gqoiog5">

            <div style={{
          position: 'relative',
          flex: '1 1 240px'
        }} data-id="element-192" data-mp="12w9k6fdbpya5">

              <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-193" data-mp="xy7w1g11j8rt" />

              <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar por texto, usuario o valor cambiado" value={busqueda} onChange={e => setBusqueda(e.target.value)} data-id="element-194" data-mp="17oj33bg0ty2x" />

            </div>

            {/* El filtro por autor solo aparece cuando hay más de uno: con un
             único usuario no filtra nada y solo ocupa sitio. */}
            {usuariosPresentes.length > 1 && <select style={{
          ...inputStyle,
          flex: '0 1 210px'
        }} value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)} data-id="element-195" data-mp="ow74mzshngy2">

                <option value="todos" data-id="element-196" data-mp="20cope2mq3v4v">Todos los usuarios</option>
                {usuariosPresentes.map(u => <option key={u} value={u} data-id="element-197" data-mp="27v507i6fcamv">
                    {u}
                  </option>)}
              </select>}
          </div>

          <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 18
      }} data-id="element-198" data-mp="2cvka1zrhz003">

            {[{
          id: 'todos',
          label: 'Todos',
          color: T.accent,
          soft: T.accentSoft
        }, ...accionesPresentes.map(a => ({
          id: a,
          label: ETIQUETA_ACCION[a],
          color: ASPECTO[a].color,
          soft: ASPECTO[a].soft
        }))].map(t => {
          const activo = filtroAccion === t.id;
          return <button key={t.id} type="button" onClick={() => setFiltroAccion(t.id)} style={{
            padding: '6px 13px',
            borderRadius: 99,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: activo ? t.color : T.textSecondary,
            background: activo ? t.soft : 'transparent',
            border: `1px solid ${activo ? `${t.color}44` : T.border}`,
            transition: 'all .15s'
          }} data-id="element-199" data-mp="2hssy2gzbty99">

                    {t.label}
                  </button>;
        })}
          </div>

          {filtrados.length === 0 ? <EmptyState title="Ningún evento coincide" hint="Prueba con otro texto o quita los filtros." icon={<Search size={22} data-id="element-201" data-mp="2axr9gx30ugme" />} data-id="element-200" data-mp="2fvsxlwfb164q" /> : <div style={{
        ...glassLight,
        overflow: 'hidden'
      }} data-id="element-202" data-mp="2auda7xpnmnj6">

              {grupos.map(grupo => <div key={grupo.dia} data-id="element-203" data-mp="33kvl3kwmdt75">
                  <div className="aud-dia" style={{
            padding: '10px 18px',
            background: 'rgba(255,252,248,.86)',
            borderBottom: `1px solid ${T.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 10
          }} data-id="element-204" data-mp="33lx020grazu5">

                    <span style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              color: T.text
            }} data-id="element-205" data-mp="1n7wxsiufuy3q">

                      {grupo.dia || 'Sin fecha'}
                    </span>
                    <span style={{
              fontSize: 11,
              color: T.textMuted,
              fontWeight: 600
            }} data-id="element-206" data-mp="2ouk7ir31gdc">

                      {grupo.items.length}{' '}
                      {grupo.items.length === 1 ? 'evento' : 'eventos'}
                    </span>
                  </div>

                  {grupo.items.map((e, i) => {
            const aspecto = ASPECTO[e.accion];
            const Icono = aspecto.icono;
            const hora = horaDe(e.sello);
            return <button key={`${grupo.dia}-${i}`} type="button" className="aud-row" onClick={() => setAbierto(e)} title="Ver el detalle de este evento" style={{
              // El riel se alinea con el centro del marcador.
              ['--riel' as string]: isMobile ? '33px' : '89px',
              gridTemplateColumns: isMobile ? '18px 30px minmax(0,1fr) 16px' : '74px 30px minmax(0,1fr) 16px',
              gap: 14,
              padding: '13px 18px'
            }} data-id="element-207" data-mp="qep0kaam1n2c" data-mpi={i}>

                        {isMobile ? <span data-id="element-208" data-mp="1a2vp9jh5xyb3" /> : <span style={{
                fontSize: 12,
                color: T.textMuted,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
                paddingTop: 7,
                textAlign: 'right'
              }} data-id="element-209" data-mp="1a2lznx015t5x">

                            {hora || '—'}
                          </span>}

                        <span className="aud-mark" title={ETIQUETA_ACCION[e.accion]} style={{
                background: aspecto.soft,
                border: `1px solid ${aspecto.color}33`,
                color: aspecto.color
              }} data-id="element-210" data-mp="1ss2d1kwtdg3r">

                          <Icono size={14} data-id="element-211" data-mp="21rfnogwocaq2" />
                        </span>

                        <div style={{
                minWidth: 0,
                paddingTop: 4
              }} data-id="element-212" data-mp="3f8k0tjuji7p">
                          <div style={{
                  fontSize: 13.5,
                  color: T.text,
                  lineHeight: 1.45,
                  fontWeight: 600,
                  wordBreak: 'break-word'
                }} data-id="element-213" data-mp="7fsic0g5orvf">

                            {e.mensaje}
                          </div>
                          <div style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: T.textMuted,
                  display: 'flex',
                  gap: 7,
                  flexWrap: 'wrap',
                  alignItems: 'center'
                }} data-id="element-214" data-mp="3m9hpa4b3q7a8">

                            {/* Quién lo hizo va primero: es lo que se busca al
                             abrir esta pantalla. */}
                            <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    fontWeight: 700,
                    color: e.usuario ? T.textSecondary : T.textMuted
                  }} data-id="element-215" data-mp="4vm5f39kdr2q">

                              {e.usuario ? <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 17,
                      height: 17,
                      borderRadius: '50%',
                      fontSize: 8.5,
                      fontWeight: 800,
                      letterSpacing: 0,
                      background: T.accentSoft,
                      color: T.brand
                    }} data-id="element-216" data-mp="2r1z481z3j17r">
                                  {iniciales(e.usuario)}
                                </span> : <User size={11} data-id="element-217" data-mp="1ye37ix9j19az" />}
                              {e.usuario || 'Sin registrar'}
                            </span>
                            <span data-id="element-218" data-mp="23do8tec65tqv">·</span>
                            <span style={{
                    color: aspecto.color,
                    fontWeight: 700
                  }} data-id="element-219" data-mp="1pd51yajfwtqa">
                              {ETIQUETA_ACCION[e.accion]}
                            </span>
                            {/* En los eventos de sesión la entidad y la acción
                             dicen lo mismo ("Inicio de sesión · Inicio de
                             sesión"): se enseña una sola vez. */}
                            {e.entidad && e.entidad !== ETIQUETA_ACCION[e.accion] ? <>
                                <span data-id="element-220" data-mp="1foq0cr2arv97">·</span>
                                <span data-id="element-221" data-mp="1frvp2570kv4e">{e.entidad}</span>
                              </> : null}
                            {e.cambios.length > 0 ? <>
                                <span data-id="element-222" data-mp="1opeermvlrxmm">·</span>
                                <span style={{
                      fontWeight: 700,
                      color: T.textSecondary
                    }} data-id="element-223" data-mp="1ood078fdot05">
                                    {e.cambios.length}{' '}
                                    {e.cambios.length === 1 ? 'campo' : 'campos'}
                                  </span>
                              </> : null}
                            {isMobile && hora ? <>
                                <span data-id="element-224" data-mp="5sb4ijwuoy2f">·</span>
                                <span data-id="element-225" data-mp="5r9i5jadwp5e">{hora}</span>
                              </> : null}
                          </div>
                        </div>

                        <ChevronRight className="aud-ir" size={15} style={{
                color: T.textMuted,
                marginTop: 9
              }} data-id="element-226" data-mp="fgt2yuhnqt2w" />

                      </button>;
          })}
                </div>)}

              <div style={{
          padding: '4px 16px 14px'
        }} data-id="element-227" data-mp="3izcu1tolnsw4">
                <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="eventos" data-id="element-228" data-mp="3nwcp79jwvb8g" />

              </div>
            </div>}
        </>}

      {abierto ? <DetalleEvento evento={abierto} onClose={() => setAbierto(null)} data-id="element-229" data-mp="thmqfaj6kwg" /> : null}
    </PageShell>;
}